/**
 * Sub-panel for the BPMN user-task properties panel when
 * `tri:uxElement === 'browser-bot'`.
 *
 * Renders:
 *   - notes textarea bound to `tri:botPrompt` (optional model hints)
 *   - target URL input
 *   - Record / Stop button (drives the browser-bot extension)
 *   - live actions table with role-name + context + inline edits
 *   - validation status + raw-JSON escape hatch
 *
 * Persists `tri:browserActions` as JSON-encoded `BrowserActionList`. The
 * BPMN modeler's existing saveXML round-trips the attribute via the
 * registered moddle extension.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserActionListSchema,
  type BrowserAction,
  type BrowserActionList,
  type ContextField,
  type ValueBinding,
} from "../../lib/bpm-protocol";
import { decodeXmlEntities } from "../../lib/decodeXmlEntities";
import {
  ping,
  startRecording,
  type Connectivity,
  type StartRecordingResult,
} from "../../lib/browserBotClient";
import {
  Clock,
  Globe,
  Keyboard,
  ListChecks,
  MousePointerClick,
  Pencil,
  Send,
  Trash2,
} from "lucide-react";

export type BrowserBotPanelProps = {
  /** Current `tri:browserActions` value as stored on the BPMN element. */
  rawActions: string | undefined;
  /** Persist a new `tri:browserActions` value (JSON-encoded list, or empty string to clear). */
  onActionsChange: (raw: string) => void;
  /** Current `tri:botPrompt` value (used as freehand notes for browser-bot tasks). */
  notes: string | undefined;
  /** Persist new notes. */
  onNotesChange: (value: string) => void;
};

type RecordingHandle = {
  result: StartRecordingResult;
  startedAt: number;
};

export function BrowserBotPanel(props: BrowserBotPanelProps) {
  const { t } = useTranslation();
  const { actions, parseError, rawValid } = useParsedActions(props.rawActions);
  const [editorOpen, setEditorOpen] = useState(false);
  const [rawDraft, setRawDraft] = useState<string>(props.rawActions ?? "");
  const [targetUrl, setTargetUrl] = useState<string>(() => deriveInitialUrl(actions));
  const [connectivity, setConnectivity] = useState<Connectivity | null>(null);
  const [recording, setRecording] = useState<RecordingHandle | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [recordError, setRecordError] = useState<string | undefined>();
  const isMountedRef = useRef(true);
  const recordingRef = useRef<RecordingHandle | null>(null);
  const activeRecordingSessionIdRef = useRef<string | null>(null);
  const progressUnsubscribeRef = useRef<(() => void) | null>(null);

  const clearProgressListener = useCallback(() => {
    if (!progressUnsubscribeRef.current) return;
    progressUnsubscribeRef.current();
    progressUnsubscribeRef.current = null;
  }, []);

  const finishRecordingSession = useCallback(
    (sessionId: string): boolean => {
      if (activeRecordingSessionIdRef.current !== sessionId) return false;
      activeRecordingSessionIdRef.current = null;
      recordingRef.current = null;
      clearProgressListener();
      if (isMountedRef.current) {
        setRecording(null);
      }
      return true;
    },
    [clearProgressListener],
  );

  const stopActiveRecording = useCallback(() => {
    const active = recordingRef.current;
    if (!active) return;
    activeRecordingSessionIdRef.current = null;
    recordingRef.current = null;
    clearProgressListener();
    active.result.stop();
    if (isMountedRef.current) {
      setRecording(null);
    }
  }, [clearProgressListener]);

  // Probe the extension on mount + whenever the panel re-mounts.
  useEffect(() => {
    let cancelled = false;
    const probe = ping();
    if (probe && typeof probe.then === "function") {
      probe.then((r) => {
        if (!cancelled) setConnectivity(r);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the raw-JSON editor draft in sync when the source attribute changes
  // externally (e.g. modeler reloads the file).
  useEffect(() => {
    setRawDraft(props.rawActions ?? "");
  }, [props.rawActions]);

  // Keep the target URL input aligned with the stored action list so
  // switching tasks (while reusing panel component state) doesn't leave a
  // stale URL from the previously selected task.
  useEffect(() => {
    setTargetUrl(deriveInitialUrl(actions));
  }, [actions]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopActiveRecording();
    };
  }, [stopActiveRecording]);

  const applyActions = useCallback(
    (next: BrowserActionList) => {
      props.onActionsChange(next.length === 0 ? "" : JSON.stringify(next));
    },
    [props],
  );

  const setAction = useCallback(
    (id: string, patch: Partial<BrowserAction>) => {
      applyActions(
        actions.map((a: BrowserAction) =>
          a.id === id ? ({ ...a, ...patch } as BrowserAction) : a,
        ),
      );
    },
    [actions, applyActions],
  );

  const removeAction = useCallback(
    (id: string) => {
      applyActions(actions.filter((a: BrowserAction) => a.id !== id));
    },
    [actions, applyActions],
  );

  const clearAllActions = useCallback(() => {
    applyActions([]);
  }, [applyActions]);

  const move = useCallback(
    (fromIdx: number, toIdx: number) => {
      if (toIdx < 0 || toIdx >= actions.length || fromIdx === toIdx) return;
      const copy = actions.slice();
      const [m] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, m!);
      applyActions(copy);
    },
    [actions, applyActions],
  );

  const addWait = useCallback(() => {
    const wait: BrowserAction = {
      id: genId(),
      recordedAt: new Date().toISOString(),
      kind: "wait",
      timeoutMs: 5000,
    };
    applyActions([...actions, wait]);
  }, [actions, applyActions]);

  // Recording controls ------------------------------------------------------

  const startRecord = useCallback(() => {
    setRecordError(undefined);
    if (!targetUrl) {
      setRecordError(t('chatBotPage.browserBot.status.noTargetUrl', 'Enter a target URL first.'));
      return;
    }
    if (recordingRef.current) return;
    const handle = startRecording(targetUrl);
    const sessionId = handle.sessionId;
    const persistRecordedActions = props.onActionsChange;
    const nextRecording = { result: handle, startedAt: Date.now() };
    recordingRef.current = nextRecording;
    activeRecordingSessionIdRef.current = sessionId;
    setRecording(nextRecording);

    progressUnsubscribeRef.current = handle.onProgress((live) => {
      // Apply only when the recording is the active one (cheap optimistic
      // reconciliation: just persist the latest snapshot).
      if (activeRecordingSessionIdRef.current !== sessionId) return;
      persistRecordedActions(live.length === 0 ? "" : JSON.stringify(live));
    });

    handle.done
      .then((finalList) => {
        if (activeRecordingSessionIdRef.current !== sessionId) return;
        persistRecordedActions(finalList.length === 0 ? "" : JSON.stringify(finalList));
        finishRecordingSession(sessionId);
      })
      .catch((e: Error) => {
        if (!finishRecordingSession(sessionId)) return;
        if (isMountedRef.current) {
          setRecordError(e.message);
        }
      });
    // We intentionally don't depend on `applyActions` here — recording flows
    // bypass the React state and write through to the modeler directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishRecordingSession, props.onActionsChange, t, targetUrl]);

  const stopRecord = useCallback(() => {
    if (!recordingRef.current) return;
    recordingRef.current.result.stop();
  }, []);

  const isConnected = !!connectivity?.ok;
  const recordButtonDisabled = !isConnected || !targetUrl || !!recording;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
      <label>
        <div style={labelStyle}>{t('chatBotPage.browserBot.labels.optionalNotes')}</div>
        <textarea
          value={props.notes ?? ""}
          onChange={(e) => props.onNotesChange(e.target.value)}
          placeholder={t('chatBotPage.browserBot.placeholders.notes')}
          rows={3}
          style={textareaStyle}
        />
      </label>

      <label>
        <div style={labelStyle}>{t('chatBotPage.browserBot.labels.targetUrl')}</div>
        <input
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
          placeholder={t('chatBotPage.browserBot.placeholders.targetUrl')}
          style={inputStyle}
          disabled={!!recording}
        />
      </label>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {recording ? (
          <button onClick={stopRecord} style={buttonStyle("danger")}>
            {t('chatBotPage.browserBot.buttons.stopRecording')}
          </button>
        ) : (
          <button onClick={startRecord} disabled={recordButtonDisabled} style={buttonStyle("primary")}>
            {t('chatBotPage.browserBot.buttons.recordActions')}
          </button>
        )}
        <RecordingStatus connectivity={connectivity} recording={recording} actionCount={actions.length} />
      </div>
      {recordError && <div style={errorStyle}>{recordError}</div>}
      {!isConnected && connectivity && (
        <ExtensionNotice connectivity={connectivity} />
      )}
      {recording && (
        <div style={hintStyle}>
          {t('chatBotPage.browserBot.status.recordingHint')}
        </div>
      )}

      <ActionsTable
        actions={actions}
        onSetAction={setAction}
        onRemove={removeAction}
        onClearAll={clearAllActions}
        onMove={move}
        expandedRowId={expandedRowId}
        onToggleExpand={(id) =>
          setExpandedRowId((prev) => (prev === id ? null : id))
        }
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={addWait} style={buttonStyle("ghost")}>
          {t('chatBotPage.browserBot.buttons.addWait')}
        </button>
        <ValidationStatus
          valid={rawValid}
          parseError={parseError}
          onOpenRaw={() => setEditorOpen((v) => !v)}
        />
      </div>

      {editorOpen && (
        <RawJsonEditor
          draft={rawDraft}
          onDraftChange={setRawDraft}
          onApply={() => props.onActionsChange(rawDraft)}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

function RecordingStatus({
  connectivity,
  recording,
  actionCount,
}: {
  connectivity: Connectivity | null;
  recording: RecordingHandle | null;
  actionCount: number;
}) {
  const { t } = useTranslation();
  if (recording) {
    return (
      <span style={{ fontSize: 12, color: "#5b008d" }}>
        {t('chatBotPage.browserBot.status.recordingProgress', { count: actionCount })}
      </span>
    );
  }
  if (connectivity === null) return <span style={hintStyle}>{t('chatBotPage.browserBot.status.checkingExtension')}</span>;
  if (connectivity.ok) return <span style={hintStyle}>{t('chatBotPage.browserBot.status.extensionConnected')}</span>;
  return null;
}

function ExtensionNotice({ connectivity }: { connectivity: Connectivity }) {
  const { t } = useTranslation();
  if (connectivity.ok) return null;
  let message = "";
  switch (connectivity.reason) {
    case "no_extension_id":
      message = t('chatBotPage.browserBot.extensionNotice.noExtensionId');
      break;
    case "no_chrome":
      message = t('chatBotPage.browserBot.extensionNotice.noChrome');
      break;
    case "no_extension":
      message = t('chatBotPage.browserBot.extensionNotice.noExtension');
      break;
    case "error":
      message = connectivity.message || t('chatBotPage.browserBot.extensionNotice.error');
      break;
  }
  return <div style={hintStyle}>{message}</div>;
}

function ActionsTable({
  actions,
  onSetAction,
  onRemove,
  onClearAll,
  onMove,
  expandedRowId,
  onToggleExpand,
}: {
  actions: BrowserActionList;
  onSetAction: (id: string, patch: Partial<BrowserAction>) => void;
  onRemove: (id: string) => void;
  onClearAll: () => void;
  onMove: (from: number, to: number) => void;
  expandedRowId: string | null;
  onToggleExpand: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (actions.length === 0) {
    return (
      <div style={emptyStyle}>
        {t('chatBotPage.browserBot.status.noActionsHint')}
      </div>
    );
  }

  const handleClearAll = () => {
    const confirmed =
      typeof window === 'undefined' ||
      window.confirm(t('chatBotPage.browserBot.confirms.clearAll'));
    if (confirmed) onClearAll();
  };

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>#</th>
          <th style={thStyle}></th>
          <th style={thStyle}>{t('chatBotPage.browserBot.tableHeader.action')}</th>
          <th style={{ ...thStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
            <button
              onClick={handleClearAll}
              style={iconButtonStyle}
              title={t('chatBotPage.browserBot.tooltips.deleteAll')}
              aria-label={t('chatBotPage.browserBot.tooltips.deleteAll')}
            >
              <Trash2 size={14} />
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {actions.map((a, idx) => (
          <React.Fragment key={a.id}>
            <ActionRow
              index={idx}
              action={a}
              onRemove={() => onRemove(a.id)}
              onMoveUp={() => onMove(idx, idx - 1)}
              onMoveDown={() => onMove(idx, idx + 1)}
              isFirst={idx === 0}
              isLast={idx === actions.length - 1}
              isExpanded={expandedRowId === a.id}
              onToggleExpand={() => onToggleExpand(a.id)}
            />
            {expandedRowId === a.id && (
              <tr>
                <td colSpan={4} style={{ ...tdStyle, padding: 0 }}>
                  <RowEditor
                    action={a}
                    onChange={(patch) => onSetAction(a.id, patch)}
                    onClose={() => onToggleExpand(a.id)}
                  />
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

function ActionRow({
  index,
  action,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  isExpanded,
  onToggleExpand,
}: {
  index: number;
  action: BrowserAction;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr>
      <td style={{ ...tdStyle, color: "#888" }}>{index + 1}</td>
      <td style={{ ...tdStyle, textAlign: "center", color: "#555" }} title={action.kind}>
        <ActionIcon kind={action.kind} />
      </td>
      <td style={tdStyle}>
        <ActionSummary action={action} />
        {action.note && (
          <div style={{ fontSize: 10, color: "#888", fontStyle: "italic", marginTop: 2 }}>
            {action.note}
          </div>
        )}
      </td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap", textAlign: "right" }}>
        <button onClick={onMoveUp} disabled={isFirst} style={iconButtonStyle} title={t('chatBotPage.browserBot.tooltips.moveUp')}>
          ↑
        </button>
        <button onClick={onMoveDown} disabled={isLast} style={iconButtonStyle} title={t('chatBotPage.browserBot.tooltips.moveDown')}>
          ↓
        </button>
        <button
          onClick={onToggleExpand}
          style={{
            ...iconButtonStyle,
            color: isExpanded ? "#5b008d" : "#555",
          }}
          title={isExpanded ? t('chatBotPage.browserBot.tooltips.closeEdit') : t('chatBotPage.browserBot.tooltips.edit')}
          aria-label="Edit row"
          aria-expanded={isExpanded}
        >
          <Pencil size={14} />
        </button>
        <button onClick={onRemove} style={iconButtonStyle} title={t('chatBotPage.browserBot.tooltips.delete')}>
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

/**
 * Render a captured action as one human-readable line. Roles get a
 * friendlier capitalised label (`button` → `Button`, `combobox` → `Dropdown`,
 * `textbox` → `Textfeld` etc.). Bindings render as a literal value, an
 * italic 🔍 description, or a 👤 context chip.
 */
function ActionSummary({ action }: { action: BrowserAction }) {
  const { t } = useTranslation();
  switch (action.kind) {
    case "navigate":
      return (
        <span>
          {t('chatBotPage.browserBot.actionSummary.navigateOpen')} <code style={urlStyle}>{action.url}</code>
        </span>
      );
    case "click":
      return (
        <span>
          {t('chatBotPage.browserBot.actionSummary.clickOn')} <Quoted>{action.label}</Quoted> ({humanRole(action.role, t)})
          {action.context && <SubtleContext context={action.context} />}
        </span>
      );
    case "type":
      return (
        <span>
          {t('chatBotPage.browserBot.actionSummary.typeVerb')} <BindingText binding={action.runtimeValue} />{' '}
          {t('chatBotPage.browserBot.actionSummary.typeInto')} <Quoted>{action.label}</Quoted>{" "}
          ({humanRole(action.role, t)})
          {action.context && <SubtleContext context={action.context} />}
        </span>
      );
    case "select":
      return (
        <span>
          {t('chatBotPage.browserBot.actionSummary.selectVerb')} <BindingText binding={action.runtimeValue} />{' '}
          {t('chatBotPage.browserBot.actionSummary.selectIn')} <Quoted>{action.label}</Quoted>{" "}
          ({humanRole(action.role, t)})
          {action.context && <SubtleContext context={action.context} />}
        </span>
      );
    case "submit":
      return action.label ? (
        <span>
          {t('chatBotPage.browserBot.actionSummary.submitForm')} <Quoted>{action.label}</Quoted>
        </span>
      ) : (
        <span>{t('chatBotPage.browserBot.actionSummary.submitForm')}</span>
      );
    case "wait":
      return (
        <span>
          {t('chatBotPage.browserBot.actionSummary.wait')}
          {action.forLabel && (
            <>
              {" "}{t('chatBotPage.browserBot.actionSummary.waitFor')} <Quoted>{action.forLabel}</Quoted>
            </>
          )}{" "}
          <span style={{ color: "#888" }}>({action.timeoutMs} ms)</span>
        </span>
      );
  }
}

function Quoted({ children }: { children: React.ReactNode }) {
  return <em style={{ fontStyle: "normal", fontWeight: 600 }}>"{children}"</em>;
}

function SubtleContext({ context }: { context: string }) {
  return (
    <span style={{ color: "#888", marginLeft: 4 }}>
      &nbsp;·&nbsp;{context}
    </span>
  );
}

function BindingText({ binding }: { binding: ValueBinding }) {
  const { t } = useTranslation();
  switch (binding.kind) {
    case "literal":
      return (
        <Quoted>{binding.value || <span style={{ color: "#888" }}>{t('chatBotPage.browserBot.binding.empty')}</span>}</Quoted>
      );
    case "description":
      return (
        <span style={{ fontStyle: "italic", color: "#5b008d" }}>
          🔍 {binding.text || <span style={{ color: "#c62828" }}>{t('chatBotPage.browserBot.binding.descriptionRequired')}</span>}
        </span>
      );
    case "context":
      return (
        <span style={{ color: "#5b008d" }}>
          👤 {binding.field}
        </span>
      );
  }
}

/**
 * Map the captured ARIA role onto a friendlier label via the i18n
 * `chatBotPage.browserBot.roles.<role>` key. Falls back to capitalising
 * the role's first letter so unknown roles stay readable.
 */
/**
 * Map a BrowserAction.kind onto a friendly noun via the i18n
 * `chatBotPage.browserBot.kinds.<kind>` keys. Used in the row editor's
 * header so the modeler sees "Klick bearbeiten" instead of "Aktion click bearbeiten".
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function humanKind(kind: string, t: any): string {
  return t(`chatBotPage.browserBot.kinds.${kind}`);
}

// i18next's TFunction has overloaded return types; using `any` here keeps
// the call sites simple.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function humanRole(role: string, t: any): string {
  const KNOWN = [
    "button","link","tab","menuitem","checkbox","radio","switch","option",
    "textbox","searchbox","combobox","listbox","spinbutton","region","main",
    "dialog","navigation","form",
  ];
  if (KNOWN.includes(role)) {
    return t(`chatBotPage.browserBot.roles.${role}`);
  }
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function ActionIcon({ kind }: { kind: BrowserAction["kind"] }) {
  const size = 14;
  switch (kind) {
    case "navigate":
      return <Globe size={size} aria-label="navigate" />;
    case "click":
      return <MousePointerClick size={size} aria-label="click" />;
    case "type":
      return <Keyboard size={size} aria-label="type" />;
    case "select":
      return <ListChecks size={size} aria-label="select" />;
    case "submit":
      return <Send size={size} aria-label="submit" />;
    case "wait":
      return <Clock size={size} aria-label="wait" />;
  }
}

/**
 * Per-row "advanced edit" form. Pencil-icon expansion exposes every field
 * that's editable for the action's kind: role / label / context for
 * pointer-target actions, url for navigate, timeoutMs / forLabel for wait,
 * the full BindingEditor for type/select.
 *
 * All fields edit a local *draft* copy. Speichern persists the draft to
 * the row via onChange + closes; Abbrechen discards the draft + closes.
 * One pair of buttons covers every field including the binding sub-editor
 * — no separate save controls anywhere else.
 */
function RowEditor({
  action,
  onChange,
  onClose,
}: {
  action: BrowserAction;
  onChange: (patch: Partial<BrowserAction>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Local draft state — mutated by every field, persisted only on Speichern.
  const [draft, setDraft] = useState<BrowserAction>(action);
  // Re-seed the draft if the underlying action changes (e.g. the modeler
  // edits the JSON elsewhere while the editor is open).
  useEffect(() => {
    setDraft(action);
  }, [action]);

  // Per-kind fields aren't in `keyof BrowserAction` (the discriminated
  // union only exposes shared keys); cast through unknown to keep the
  // helper generic across all variants.
  const setField = (key: string, value: unknown) =>
    setDraft((prev) => ({ ...prev, [key]: value } as BrowserAction));

  const onSave = () => {
    onChange(draft as unknown as Partial<BrowserAction>);
    onClose();
  };

  return (
    <div style={rowEditorStyle}>
      <div style={rowEditorHeader}>
        <span>
          {t('chatBotPage.browserBot.editorHeader', { kind: humanKind(action.kind, t) })}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onClose} style={miniBtn}>
            {t('chatBotPage.browserBot.buttons.cancel')}
          </button>
          <button
            onClick={onSave}
            style={{ ...miniBtn, background: "#5b3a87", color: "white", borderColor: "#5b3a87" }}
          >
            {t('chatBotPage.browserBot.buttons.save')}
          </button>
        </div>
      </div>

      <div style={rowEditorGrid}>
        {draft.kind === "navigate" && (
          <Field label={t('chatBotPage.browserBot.labels.url')}>
            <input
              value={draft.url}
              onChange={(e) => setField("url", e.target.value)}
              style={inlineInputStyle}
            />
          </Field>
        )}

        {(draft.kind === "click" ||
          draft.kind === "type" ||
          draft.kind === "select") && (
          <>
            <Field label={t('chatBotPage.browserBot.labels.role')}>
              <input
                value={draft.role}
                onChange={(e) => setField("role", e.target.value)}
                style={inlineInputStyle}
              />
            </Field>
            <Field label={t('chatBotPage.browserBot.labels.elementLabel')}>
              <input
                value={draft.label}
                onChange={(e) => setField("label", e.target.value)}
                style={inlineInputStyle}
              />
            </Field>
            <Field label={t('chatBotPage.browserBot.labels.context')}>
              <input
                value={draft.context ?? ""}
                onChange={(e) => setField("context", e.target.value || undefined)}
                placeholder={t('chatBotPage.browserBot.placeholders.context')}
                style={inlineInputStyle}
              />
            </Field>
          </>
        )}

        {draft.kind === "type" && (
          <Field label={t('chatBotPage.browserBot.labels.capturedValue')}>
            <input value={draft.capturedValue} readOnly style={{ ...inlineInputStyle, color: "#888" }} />
          </Field>
        )}
        {draft.kind === "select" && (
          <Field label={t('chatBotPage.browserBot.labels.capturedOption')}>
            <input value={draft.option} readOnly style={{ ...inlineInputStyle, color: "#888" }} />
          </Field>
        )}

        {draft.kind === "submit" && (
          <Field label={t('chatBotPage.browserBot.labels.formLabel')}>
            <input
              value={draft.label ?? ""}
              onChange={(e) => setField("label", e.target.value || undefined)}
              style={inlineInputStyle}
            />
          </Field>
        )}

        {draft.kind === "wait" && (
          <>
            <Field label={t('chatBotPage.browserBot.labels.waitFor')}>
              <input
                value={draft.forLabel ?? ""}
                onChange={(e) => setField("forLabel", e.target.value || undefined)}
                placeholder={t('chatBotPage.browserBot.placeholders.waitFor')}
                style={inlineInputStyle}
              />
            </Field>
            <Field label={t('chatBotPage.browserBot.labels.timeout')}>
              <input
                type="number"
                min={100}
                step={100}
                value={draft.timeoutMs}
                onChange={(e) =>
                  setField("timeoutMs", Math.max(100, Number(e.target.value) || 5000))
                }
                style={inlineInputStyle}
              />
            </Field>
          </>
        )}

        <Field label={t('chatBotPage.browserBot.labels.note')}>
          <input
            value={draft.note ?? ""}
            onChange={(e) => setField("note", e.target.value || undefined)}
            placeholder={t('chatBotPage.browserBot.placeholders.noteOptional')}
            style={inlineInputStyle}
          />
        </Field>
      </div>

      {/* Runtime value gets its own full-width row underneath the grid:
          tabs + helper text + input wouldn't fit comfortably in a single
          column, and it's the most important per-action edit. */}
      {(draft.kind === "type" || draft.kind === "select") && (
        <div style={{ marginTop: 10 }}>
          <Field label={t('chatBotPage.browserBot.labels.runtimeValue')}>
            <BindingEditor
              binding={draft.runtimeValue}
              capturedValue={draft.kind === "type" ? draft.capturedValue : draft.option}
              onCommit={(b) => setField("runtimeValue", b)}
            />
          </Field>
        </div>
      )}

    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#555" }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

// (Former ValueCell + BindingChip removed — the table now renders a single
// readable summary per row via ActionSummary; all editing happens through
// the pencil-icon RowEditor below.)

/**
 * Inline binding editor — two tabs now: literal and description. Session
 * data (userId/tenantId/locale/timezone) flows into processVariables under
 * `__session.*` and is referenced via a regular description binding, so
 * we no longer expose a dedicated "context" UI.
 *
 * Legacy `{kind: "context", field}` bindings still parse from existing
 * BPMN files. They're rendered as a synthesized description so the user
 * can edit/save without losing the intent; the next save persists them
 * as a normal description binding.
 */
function BindingEditor({
  binding,
  capturedValue,
  onCommit,
}: {
  binding: ValueBinding;
  capturedValue: string;
  onCommit: (b: ValueBinding) => void;
}) {
  const { t } = useTranslation();

  // Normalize the binding for display: legacy context bindings present as
  // a description so the editor can show them in the runtime tab.
  const effective: ValueBinding =
    binding.kind === "context"
      ? { kind: "description", text: legacyContextDescription(binding.field, t) }
      : binding;

  const switchTab = (kind: "literal" | "description") => {
    if (kind === effective.kind) return;
    if (kind === "literal") {
      onCommit({
        kind: "literal",
        value: effective.kind === "description" ? "" : capturedValue,
      });
    } else {
      onCommit({
        kind: "description",
        text: binding.kind === "context" ? legacyContextDescription(binding.field, t) : "",
      });
    }
  };

  const showDescriptionWarning =
    effective.kind === "description" && effective.text.trim().length === 0;

  return (
    <div style={editorStyle}>
      <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
        {(["literal", "description"] as const).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => switchTab(tabKey)}
            style={{
              ...miniTab,
              fontWeight: effective.kind === tabKey ? 600 : 400,
              borderBottom: effective.kind === tabKey ? "2px solid #5b008d" : "2px solid transparent",
            }}
          >
            {tabKey === "literal"
              ? t('chatBotPage.browserBot.tabs.literal')
              : t('chatBotPage.browserBot.tabs.runtime')}
          </button>
        ))}
      </div>
      {effective.kind === "literal" && (
        <>
          <div style={tabHintStyle}>{t('chatBotPage.browserBot.tabHints.literal')}</div>
          <input
            value={effective.value}
            onChange={(e) => onCommit({ kind: "literal", value: e.target.value })}
            placeholder={capturedValue}
            style={inlineInputStyle}
          />
        </>
      )}
      {effective.kind === "description" && (
        <>
          <div style={tabHintStyle}>{t('chatBotPage.browserBot.tabHints.description')}</div>
          <input
            value={effective.text}
            onChange={(e) => onCommit({ kind: "description", text: e.target.value })}
            placeholder={t('chatBotPage.browserBot.placeholders.description')}
            style={inlineInputStyle}
          />
        </>
      )}
      {showDescriptionWarning && (
        <p style={{ fontSize: 10, color: "#c62828", margin: "4px 0 0" }}>
          {t('chatBotPage.browserBot.status.descriptionRequired')}
        </p>
      )}
    </div>
  );
}

/**
 * Synthesize an editable description for legacy `{kind: "context", field}`
 * bindings. Uses the localized field labels so the modeler sees a German
 * description in a German UI.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function legacyContextDescription(field: ContextField, t: any): string {
  return t('chatBotPage.browserBot.legacyContextDescription', {
    field: t(`chatBotPage.browserBot.contextFields.${field}`),
  });
}

function ValidationStatus({
  valid,
  parseError,
  onOpenRaw,
}: {
  valid: boolean;
  parseError: string | undefined;
  onOpenRaw: () => void;
}) {
  const { t } = useTranslation();
  if (valid) {
    return <span style={{ fontSize: 11, color: "#388e3c" }}>{t('chatBotPage.browserBot.status.valid')}</span>;
  }
  return (
    <span style={{ fontSize: 11, color: "#c62828" }}>
      {t('chatBotPage.browserBot.status.schemaMismatch', { error: parseError })}{" "}
      <button onClick={onOpenRaw} style={linkButtonStyle}>
        {t('chatBotPage.browserBot.buttons.editRawJson')}
      </button>
    </span>
  );
}

function RawJsonEditor({
  draft,
  onDraftChange,
  onApply,
  onClose,
}: {
  draft: string;
  onDraftChange: (s: string) => void;
  onApply: () => void;
  onClose: () => void;
}) {
  return (
    <div style={{ border: "1px solid #ccc", borderRadius: 4, padding: 8 }}>
      <textarea
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        rows={8}
        style={{ ...textareaStyle, fontFamily: "monospace", fontSize: 11 }}
      />
      <RawJsonEditorButtons onApply={onApply} onClose={onClose} />
    </div>
  );
}

function RawJsonEditorButtons({ onApply, onClose }: { onApply: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
      <button onClick={onApply} style={buttonStyle("primary")}>
        {t('chatBotPage.browserBot.buttons.apply')}
      </button>
      <button onClick={onClose} style={buttonStyle("ghost")}>
        {t('chatBotPage.browserBot.buttons.close')}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function useParsedActions(raw: string | undefined): {
  actions: BrowserActionList;
  parseError: string | undefined;
  rawValid: boolean;
} {
  return useMemo(() => {
    if (!raw || raw.trim() === "") {
      return { actions: [], parseError: undefined, rawValid: true };
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (firstErr) {
      // bpmn-js sometimes hands back HTML-entity-encoded payloads
      // (`&#34;` instead of `"`) for custom moddle attributes. Try the
      // decoded form before reporting the parse failure.
      try {
        parsed = JSON.parse(decodeXmlEntities(raw));
      } catch {
        return {
          actions: [],
          parseError: `JSON parse error: ${(firstErr as Error).message}`,
          rawValid: false,
        };
      }
    }
    const result = BrowserActionListSchema.safeParse(parsed);
    if (!result.success) {
      const issue = result.error.issues[0];
      const path = issue?.path?.join(".") ?? "(root)";
      return {
        actions: [],
        parseError: `${path}: ${issue?.message ?? "schema mismatch"}`,
        rawValid: false,
      };
    }
    return { actions: result.data, parseError: undefined, rawValid: true };
  }, [raw]);
}

function deriveInitialUrl(actions: BrowserActionList): string {
  for (const a of actions) {
    if (a.kind === "navigate") return a.url;
  }
  return "";
}

function genId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/* -------------------------------------------------------------------------- */
/* Styles                                                                     */
/* -------------------------------------------------------------------------- */

const labelStyle: React.CSSProperties = { fontSize: 12, color: "#555", marginBottom: 4 };
const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: 6,
  fontFamily: "inherit",
  border: "1px solid #ccc",
  borderRadius: 3,
};
const textareaStyle: React.CSSProperties = { ...inputStyle, resize: "vertical" };
const inlineInputStyle: React.CSSProperties = {
  ...inputStyle,
  padding: 2,
  fontSize: 12,
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 4px",
  borderBottom: "1px solid #ddd",
  fontSize: 11,
  color: "#666",
  fontWeight: 600,
};
const tdStyle: React.CSSProperties = {
  padding: "4px",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};
const emptyStyle: React.CSSProperties = {
  padding: 12,
  border: "1px dashed #ccc",
  borderRadius: 4,
  color: "#666",
  fontSize: 12,
};
const hintStyle: React.CSSProperties = { fontSize: 11, color: "#888" };
const errorStyle: React.CSSProperties = { fontSize: 12, color: "#c62828" };
const iconButtonStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  padding: "2px 6px",
  cursor: "pointer",
};
const linkButtonStyle: React.CSSProperties = {
  ...iconButtonStyle,
  color: "#5b008d",
  textDecoration: "underline",
  padding: 0,
};
const editorStyle: React.CSSProperties = {
  border: "1px solid #ccc",
  borderRadius: 3,
  padding: 4,
  background: "#fff",
  minWidth: 240,
};
const urlStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#5b008d",
  wordBreak: "break-all",
};
const rowEditorStyle: React.CSSProperties = {
  background: "#f7f4fa",
  border: "1px solid #d8c3e2",
  borderRadius: 3,
  padding: 8,
  margin: "2px 0 6px 0",
};
const rowEditorHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: 11,
  color: "#5b008d",
  marginBottom: 8,
};
const rowEditorGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "6px 12px",
};
const tabHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#666",
  marginBottom: 6,
  lineHeight: 1.4,
};
const miniTab: React.CSSProperties = {
  border: "none",
  background: "transparent",
  padding: "2px 6px",
  fontSize: 11,
  cursor: "pointer",
};
const miniBtn: React.CSSProperties = {
  border: "1px solid #ccc",
  background: "#fafafa",
  padding: "2px 8px",
  borderRadius: 3,
  cursor: "pointer",
};
function buttonStyle(variant: "primary" | "ghost" | "danger"): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: "6px 12px",
    borderRadius: 3,
    border: "1px solid #ccc",
    fontSize: 13,
    cursor: "pointer",
  };
  if (variant === "primary") {
    return { ...base, background: "#5b008d", color: "#fff", border: "1px solid #5b008d" };
  }
  if (variant === "danger") {
    return { ...base, background: "#c62828", color: "#fff", border: "1px solid #c62828" };
  }
  return { ...base, background: "transparent", color: "#000" };
}
